import { useState } from "react";
import { useDispatch } from "react-redux";

/**
 * A reusable hook to handle search and pagination logic.
 * @param {Object} options
 * @param {Function} options.searchThunk - thunk for searching
 * @param {Function} options.listThunk - thunk for regular listing
 * @param {string} options.currentPath - current route path
 * @param {string} options.role - user role
 * @param {string} options.lIUId - login user id
 * @param {Function} options.setFilteredData - state setter for filtered data
 * @param {Function} options.setStoreTotalCount - state setter for total count
 * @param {Function} [options.getExtraPayload] - optional callback to inject additional search payload (e.g. dates, status)
 */
export const useSearchAndPagination = ({
  searchThunk,
  listThunk,
  currentPath,
  role,
  lIUId,
  setFilteredData,
  setStoreTotalCount,
  getExtraPayload,
  status,
  month,
  onSearchStatusChange,
}) => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
  });

  const handleGlSearch = async ({
    source,
    value,
    page = 1,
    pageSize = process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
  }) => {
    setSearchText(value);
    setShowLoader(true);
    onSearchStatusChange?.(true, 0);

    try {
      const extraPayload = getExtraPayload ? getExtraPayload() : {};
      const payload = {
        route: currentPath,
        searchIn:
          source === process.env.REACT_APP_DIRECTDETAILPAGERENDEREDID
            ? currentPath?.split("/")?.[0]
            : currentPath.replace("/", ""),
        searchKeyword: value,
        role,
        lIUId,
        status,
        month,
        fetchWithoutPagination: "no", // ✅ now paginate search results
        page,
        pageSize,
        ...extraPayload,
      };

      const response = await dispatch(searchThunk(payload)).unwrap();
      const resultList = response?.list || [];
      const totalCount = response?.totalCount || 0;

      //  Edge Case: If paged data is empty but results exist on earlier pages
      if (resultList.length === 0 && totalCount > 0 && page > 1) {
        console.warn("⚠ Empty page fallback → Fetch page 1");
        return handleGlSearch({ source, value, page: 1, pageSize });
      }

      // Normal state update
      setFilteredData?.(resultList);
      setStoreTotalCount?.(totalCount);
      setPagination?.({ current: page, pageSize });
      onSearchStatusChange?.(true, totalCount);
    } catch (error) {
      console.error(" Search failed", error);
      setFilteredData([]);
      setStoreTotalCount(0);
      onSearchStatusChange?.(false, 0);
    } finally {
      setShowLoader(false);
    }
  };

  const handlePaginationChange = async (page, pageSize) => {
    const trimmedSearch = searchText && searchText;

    setPagination({ current: page, pageSize });

    if (trimmedSearch) {
      await handleGlSearch({
        value: trimmedSearch,
        page,
        pageSize,
      });
    } else {
      const fetchPayload =
        currentPath === process.env.REACT_APP_DASHBOARD_PR_SEARCH_IN ||
        currentPath === process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN
          ? {
              route: currentPath,
              allStates: {
                pagination: {
                  current: page,
                  page: page,
                  pageSize,
                },
                status,
                filterValues: month || {},
                searchKeyword: "",
              },
            }
          : {
              route: currentPath,
              pagination: { current: page, pageSize },
              role,
              lIUId,
              status,
              month,
              fetchWithoutPagination: "no",
            };

      try {
        const response = await dispatch(listThunk(fetchPayload)).unwrap();
        setFilteredData?.(response?.list || response?.data || []);
        setStoreTotalCount?.(response?.totalCount || 0);
      } catch (error) {
        console.error("Pagination list fetch failed", error);
        setFilteredData?.([]);
        setStoreTotalCount?.(0);
      }
    }
  };

  return {
    handleGlSearch,
    handlePaginationChange,
    searchText,
    showLoader,
    pagination,
    setPagination,
  };
};
